<script setup lang="ts">
import type Trip from '@/classes/trip_classes/trip'
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import {
  ActionTypes,
  AsyncStatus,
  GettersTypes,
  MutationTypes,
  type State,
} from '@/store/store_types'
import getAssetSrc from '@/utils/getAssetSrc'
import { getRecommendedVehiclesFromTripComparisons } from '@/utils/recommendVehicles'
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import TripDetailsSection from '../trips/planning/TripDetailsSection.vue'
import TripStatsContent from '../trips/statistics/TripStatsContent.vue'
import ElevatedBtn from '../ui-elements/buttons/ElevatedBtn.vue'
import DescriptiveLoader from '../ui-elements/loaders/DescriptiveLoader.vue'

const store = useStore<State>()

const status = computed(() => store.state.creatingSalesLeadStatus)
watch(status, () => {
  if (status.value === AsyncStatus.SUCCESS) {
    // close modal after success
    setTimeout(() => {
      store.commit(MutationTypes.setMainDialogContent, undefined)
    }, 2000)
  }
})

const selectedTrip = computed<Trip | undefined>(() => store.getters[GettersTypes.selectedTripData])

async function connectWithSales() {
  store.dispatch(ActionTypes.createSalesLead)
}

const recommendations = computed<Vehicle[]>(() => {
  if (!selectedTrip.value) return [] // guard clause
  return getRecommendedVehiclesFromTripComparisons(selectedTrip.value)
})

function imageSrc(vehicle: Vehicle): string {
  return (
    vehicle?.imageSrcPaths?.[0] ??
    vehicle?.evModel?.imageSrc ??
    getAssetSrc('car_images/placeholder_car.png')
  )
}
</script>

<template>
  <v-card v-if="status === AsyncStatus.PROCESSING" flat color="transparent">
    <DescriptiveLoader message="connecting with sales" />
  </v-card>
  <v-card v-else-if="status === AsyncStatus.FAILED" flat color="transparent" class="pa-5 pa-md-10">
    <v-alert type="error"> Unable to connect with sales. Please try again. </v-alert>
  </v-card>
  <v-card v-else-if="status === AsyncStatus.SUCCESS" flat color="transparent" class="pa-5 pa-md-10">
    <v-alert type="success"> Successfully connected with sales. </v-alert>
  </v-card>
  <v-card v-else flat color="transparent" class="px-3 px-md-5 px-lg-10">
    <v-card flat color="transparent" v-if="selectedTrip">
      <v-row no-gutters>
        <!-- trip details section -->
        <TripDetailsSection :trip="selectedTrip" hide-btns class="flex-grow-1 flex-shrink-1" />
        <ElevatedBtn @click="connectWithSales" class="flex-grow-0 flex-shrink-0">Connect with sales</ElevatedBtn>
      </v-row>
      <!-- recommended vehicles section -->
      <v-card-title
        v-if="recommendations.length > 0"
        style="font-size: 16px"
        class="font-weight-bold"
      >
        Top {{ recommendations.length > 1 ? recommendations.length + ' ' : '' }}recommended
        available vehicle{{ recommendations.length > 1 ? 's' : '' }}
      </v-card-title>
      <v-row no-gutters class="px-4 mb-8" v-if="recommendations.length > 0">
        <v-card
          v-for="(item, index) in recommendations"
          elevation="3"
          width="120px"
          class="ma-2"
          :class="index == 0 ? 'ml-1' : ''"
          rounded="large"
        >
          <v-card-text class="pt-0">
            <v-img
              contain
              :src="imageSrc(item)"
              aspect-ratio="1"
              width="100%"
              height="100%"
              max-height="50%"
            ></v-img>
            <p v-if="item.licensePlate" class="mb-0 font-weight-bold">{{ item.licensePlate }}</p>
            {{ item.evModel ? item.evModel.name : 'combustion vehicle' }}
          </v-card-text>
        </v-card>
      </v-row>
      <!-- vehicles comparison section -->
      <v-row no-gutters>
        <v-col md="6">
          <v-card flat color="transparent">
            <v-card-title style="font-size: 16px" class="font-weight-bold">
              Current vehicle
            </v-card-title>
            <TripStatsContent large />
          </v-card>
        </v-col>
        <v-col md="6">
          <v-card flat color="transparent">
            <v-card-title style="font-size: 16px" class="font-weight-bold">
              Comparison vehicle
            </v-card-title>
          </v-card>
          <TripStatsContent comparison large />
        </v-col>
      </v-row>
    </v-card>
    <v-card flat color="transparent" v-else>
      <DescriptiveLoader />
    </v-card>
  </v-card>
</template>
